import { useGetFileLink, useUploadFile } from '@/hooks/apiHooks';
import { uuid4 } from '@sentry/utils';

export interface ExtendFile {
  id: string;
  name: string;
  path?: string;
  preview?: string;
  status: 'new' | 'deleted' | 'default';
  type: string;
  isUploaded: boolean;
  file?: File;
  uuid?: string;
}

const FORMAT_PDF = ['pdf'];
const FORMAT_TEXT = ['txt'];
const FORMAT_HWP = ['hwp', 'hwpx'];
const FORMAT_PHOTOSHOP = ['psd'];
const FORMAT_WORD = ['doc', 'docx'];
const FORMAT_EXCEL = ['xls', 'xlsx'];
const FORMAT_ZIP = ['zip', 'rar', 'iso'];
const FORMAT_ILLUSTRATOR = ['ai', 'esp'];
const FORMAT_POWERPOINT = ['ppt', 'pptx'];
const FORMAT_AUDIO = ['wav', 'aif', 'mp3', 'aac'];
const FORMAT_IMG = ['jpg', 'jpeg', 'gif', 'bmp', 'png', 'svg', 'webp'];
const FORMAT_VIDEO = ['m4v', 'avi', 'mpg', 'mp4', 'webm', 'mov'];

const iconUrl = (icon: string) => `/assets/icons/files/${icon}.svg`;

// ----------------------------------------------------------------------

export function getFileFormat(type: string) {
  let format;

  if (FORMAT_TEXT.includes(type)) {
    format = 'txt';
  } else if (FORMAT_ZIP.includes(type)) {
    format = 'zip';
  } else if (FORMAT_HWP.includes(type)) {
    format = 'hwp';
  } else if (FORMAT_AUDIO.includes(type)) {
    format = 'audio';
  } else if (FORMAT_IMG.includes(type)) {
    format = 'image';
  } else if (FORMAT_VIDEO.includes(type)) {
    format = 'video';
  } else if (FORMAT_WORD.includes(type)) {
    format = 'word';
  } else if (FORMAT_EXCEL.includes(type)) {
    format = 'excel';
  } else if (FORMAT_POWERPOINT.includes(type)) {
    format = 'powerpoint';
  } else if (FORMAT_PDF.includes(type)) {
    format = 'pdf';
  } else if (FORMAT_PHOTOSHOP.includes(type)) {
    format = 'photoshop';
  } else if (FORMAT_ILLUSTRATOR.includes(type)) {
    format = 'illustrator';
  } else {
    format = fileTypeByUrl(type);
  }

  return format;
}

export function fileFormat(fileUrl: string | undefined) {
  return getFileFormat(fileTypeByUrl(fileUrl));
}

// ----------------------------------------------------------------------

export function fileThumb(fileUrl: string) {
  let thumb;

  switch (fileFormat(fileUrl)) {
    case 'folder':
      thumb = iconUrl('ic_folder');
      break;
    case 'txt':
      thumb = iconUrl('ic_txt');
      break;
    case 'zip':
      thumb = iconUrl('ic_zip');
      break;
    case 'audio':
      thumb = iconUrl('ic_audio');
      break;
    case 'video':
      thumb = iconUrl('ic_video');
      break;
    case 'word':
      thumb = iconUrl('ic_word');
      break;
    case 'excel':
      thumb = iconUrl('ic_excel');
      break;
    case 'powerpoint':
      thumb = iconUrl('ic_power_point');
      break;
    case 'pdf':
      thumb = iconUrl('ic_pdf');
      break;
    case 'photoshop':
      thumb = iconUrl('ic_pts');
      break;
    case 'illustrator':
      thumb = iconUrl('ic_ai');
      break;
    case 'image':
      thumb = iconUrl('ic_img');
      break;
    default:
      thumb = iconUrl('ic_file');
  }
  return thumb;
}

// ----------------------------------------------------------------------

export function fileTypeByUrl(fileUrl = '') {
  return (fileUrl && fileUrl.split('.').pop()) || '';
}

// ----------------------------------------------------------------------

export function fileNameByUrl(fileUrl: string) {
  return fileUrl.split('/').pop();
}

export default function getFileData(file: ExtendFile | string, index?: number) {
  if (typeof file === 'string') {
    return {
      key: index ? `${file}-${index}` : file,
      name: fileNameByUrl(file) ?? '',
      path: file,
      type: fileTypeByUrl(file),
      preview: file,
    };
  }

  return {
    key: index ? `${file.name}-${index}` : file.name,
    name: file.name,
    path: file.path,
    type: file.type,
    preview: file.preview,
  };
}

export function validateFile(
  files: File[],
  options: {
    maxSize?: number;
    maxLength?: number;
    isOnlyImage?: boolean;
    existFileLength?: number;
  },
) {
  const { maxSize, maxLength, isOnlyImage, existFileLength } = options;

  if (isOnlyImage && files.some((file) => !file.type.startsWith('image/'))) {
    throw new Error('해당 파일 형식은 지원되지 않습니다.');
  }

  if (maxSize && files.some((file) => file.size > maxSize)) {
    const formatFileSize = (sizeInBytes: number): string => {
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let size = sizeInBytes;
      let unitIndex = 0;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }

      if (unitIndex === units.length - 1 && size >= 1024) {
        return '';
      }

      return `${size.toFixed(2)}${units[unitIndex]}`;
    };

    const convertedSize = formatFileSize(maxSize);
    throw new Error(
      convertedSize
        ? `파일 용량이 너무 큽니다. 최대 ${convertedSize}까지 업로드할 수 있습니다.`
        : `첨부한 파일이 용량 제한을 초과했어요. 파일 용량 확인 후 다시 시도해 주세요.`,
    );
  }

  if (maxLength && (existFileLength || 0) + files.length > maxLength) {
    throw new Error(`최대 ${maxLength}개의 파일만 업로드 가능합니다.`);
  }
}

export const getMimeTypeByExtension = (filename: string) => {
  const mimeTypes: { [key: string]: string } = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.pdf': 'application/pdf',
    '.txt': 'text/plain',
    '.html': 'text/html',
    '.css': 'text/css',
    '.js': 'application/javascript',
    '.json': 'application/json',
    '.csv': 'text/csv',
    '.mp4': 'video/mp4',
    '.mp3': 'audio/mpeg',
    '.wav': 'audio/wav',
    '.zip': 'application/zip',
    '.rar': 'application/vnd.rar',
    '.7z': 'application/x-7z-compressed',
  };
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  return mimeTypes[ext] || 'application/octet-stream';
};

export const base64ToFile = (base64String: string, fileName: string): File => {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
};

export const uploadImage = async (blob: File | Blob) => {
  const uploadData = await useUploadFile(blob as File);
  const downLoadData = await useGetFileLink(uploadData.data.uuid);
  return {
    url: downLoadData.data.url,
    name: uploadData.data.fileName,
    uuid: uploadData.data.uuid,
  };
};

export type RemoteFile = {
  readonly link: string;
  readonly name: string;
  readonly uuid?: string;
  readonly fileUuid?: string;
  readonly fileSize?: number | string;
  readonly createdAt?: string | Date;
  readonly updatedAt?: string | Date;
};

export const remoteFileToExtendFile = (remoteFile: RemoteFile): ExtendFile => {
  return {
    id: uuid4(),
    name: remoteFile.name,
    path: remoteFile.link,
    preview: remoteFile.link,
    status: 'default',
    isUploaded: true,
    type: getMimeTypeByExtension(remoteFile.name),
  };
};

export const extendFileToRemoteFile = (files: ExtendFile[]) =>
  files
    .filter(({ isUploaded, status }) => isUploaded && status !== 'deleted')
    .map((file) => ({
      name: file.name ?? '',
      link: file.path ?? '',
    }));
